import { selectors } from 'data'
import { notifyError } from 'data/App/sagas'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import * as banktransfer from 'services/api/payments/banktransfer'
import getSettlementAction from 'utils/getSettlementAction'
import isLocal from 'utils/isLocal'

import {
  BlockBankAction,
  CreateBankLinkAction,
  FetchBankAction,
  FetchBankLinkTokenForRefreshAction,
  FetchBankSettlementAction,
  UpdateBankLinkAction
} from './actions'
import * as actions from './actions'
import {
  BLOCK_BANK,
  CREATE_BANK_LINK,
  FETCH_BANK_LINK_TOKEN_FOR_REFRESH,
  FETCH_BANK_SETTLEMENT,
  FETCH_BANK_TYPE,
  FETCH_BANKS_TYPE,
  UPDATE_BANK_LINK
} from './actionTypes'

const fetchBanksSaga = function* () {
  yield put(actions.setFetchingBanks(true))
  const data = yield call(banktransfer.getBanks)
  yield put(actions.setFetchingBanks(false))

  if (data.error) {
    return yield put(actions.setFetchBanksError(data.error.cause))
  }
  yield put(actions.setBanks(data.response))
}

const createBankLinkSaga = function* (action: CreateBankLinkAction) {
  const config = yield select(selectors.config.getConfig)
  const redirect_uri = `https://${
    isLocal() ? config?.exchangeLocalHostUrlBase : window.location.host
  }/plaid`

  const data = yield call(banktransfer.createBankAccountLink, {
    attributes: config.plaidSupported
      ? {
          redirect_uri,
          supportedPartners: ['PLAID', 'YAPILY']
        }
      : undefined,
    currency: action.payload.currency
  })
  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return
  }

  yield put(actions.createBankLinkSuccess(data.response))
}

const fetchBankLinkTokenForRefreshSaga = function* (action: FetchBankLinkTokenForRefreshAction) {
  const config = yield select(selectors.config.getConfig)
  const redirect_uri = `https://${
    isLocal() ? config?.exchangeLocalHostUrlBase : window.location.host
  }/plaid`

  const data = yield call(
    banktransfer.fetchBankLinkTokenForRefresh,
    action.payload.bankId,
    {
      attributes: redirect_uri
    },
    config?.openBankingCallback ?? ''
  )

  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return
  }

  yield put(actions.createBankLinkSuccess(data.response))
}

const updateBankLinkSaga = function* (action: UpdateBankLinkAction) {
  const config = yield select(selectors.config.getConfig)
  const data = yield call(
    banktransfer.updateBankAccountLink,
    action.payload.bankId,
    action.payload.attributes,
    config.openBankingCallback
  )
  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return
  }

  yield put(actions.updateBankLinkSuccess(data.response))
}

const fetchBankSaga = function* (action: FetchBankAction) {
  const bankId = action.payload.id
  yield put(actions.setFetchingBank(bankId, true))
  const data = yield call(banktransfer.getBank, bankId)
  yield put(actions.setFetchingBank(bankId, false))

  if (data.error) {
    return yield put(actions.setFetchBankError(bankId, data.error.cause))
  }

  yield put(actions.setBank(bankId, data.response))
}

const fetchBankSettlementSaga = function* (action: FetchBankSettlementAction) {
  const config = yield select(selectors.config.getConfig)
  const data = yield call(
    banktransfer.updateBankAccountLink,
    action.payload.bank.id,
    action.payload.attributes,
    config?.openBankingCallback ?? ''
  )
  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return
  }

  const response = getSettlementAction(
    data.response.attributes.settlementResponse,
    action.payload.bank
  )

  const { bank, message, returnedAction, settlementResponse } = response

  if (response.message) {
    // Set bank settlement if issue found
    yield put(returnedAction(settlementResponse, message, bank))
  } else {
    // Submit deposit if bank is cleared
    yield put(returnedAction(settlementResponse.amount, bank.currency, bank))
  }
}

const blockBankSaga = function* (action: BlockBankAction) {
  const bankId = action.payload.id
  yield put(actions.setFetchingBanks(true))
  const data = yield call(banktransfer.blockBank, bankId)
  yield put(actions.setFetchingBanks(false))

  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return
  }

  yield put(actions.blockBankSuccess(bankId))
  // refresh banks
  yield put(actions.fetchBanks())
}

export default function* methodsSagas() {
  yield takeEvery(FETCH_BANKS_TYPE, fetchBanksSaga)
  yield takeEvery(CREATE_BANK_LINK, createBankLinkSaga)
  yield takeEvery(UPDATE_BANK_LINK, updateBankLinkSaga)
  yield takeEvery(FETCH_BANK_TYPE, fetchBankSaga)
  yield takeEvery(FETCH_BANK_LINK_TOKEN_FOR_REFRESH, fetchBankLinkTokenForRefreshSaga)
  yield takeEvery(BLOCK_BANK, blockBankSaga)
  yield takeEvery(FETCH_BANK_SETTLEMENT, fetchBankSettlementSaga)
}
